import React, { useState } from 'react';
import { useLeadStore } from '@/store/useLeadStore';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Modal } from '../UI/Modal';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { validateData } from '@/utils/validation';
import { step3Schema } from '@/config/validation.schemas';

export const Step3DetailsBien: React.FC = () => {
  const { formData, setAddress, setSurface, setSurfaceRdc, setSurfaceSousSol, setIsNewProperty, nextStep, goToStep, addressValidated } = useLeadStore();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showModal, setShowModal] = useState(false);
  const [surface, setSurfaceLocal] = useState(formData.surface?.toString() || '');
  const [surfaceRdc, setSurfaceRdcLocal] = useState(formData.surfaceRdc?.toString() || '');
  const [surfaceSousSol, setSurfaceSousSolLocal] = useState(formData.surfaceSousSol?.toString() || '');
  const [floor, setFloorLocal] = useState(formData.floor?.toString() || '');
  const [isNewProperty, setIsNewPropertyLocal] = useState(formData.isNewProperty);

  const isCommerce = formData.propertyType === 'commerce';
  const isBureaux = formData.propertyType === 'bureaux';

  const handleAddressChange = (value: string, validated: boolean, inseeCode?: string) => {
    setAddress(value, validated, inseeCode);
    if (errors.address) {
      setErrors((prev) => ({ ...prev, address: '' }));
    }
  };

  const handleSurfaceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSurfaceLocal(value);
    if (errors.surface) {
      setErrors((prev) => ({ ...prev, surface: '' }));
    }
  };

  const handleSurfaceRdcChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSurfaceRdcLocal(value);
    if (errors.surfaceRdc) {
      setErrors((prev) => ({ ...prev, surfaceRdc: '' }));
    }
  };

  const handleSurfaceSousSolChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSurfaceSousSolLocal(value);
    if (errors.surfaceSousSol) {
      setErrors((prev) => ({ ...prev, surfaceSousSol: '' }));
    }
  };

  const handleFloorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFloorLocal(value);
    if (errors.floor) {
      setErrors((prev) => ({ ...prev, floor: '' }));
    }
  };

  const handleIsNewPropertyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsNewPropertyLocal(e.target.checked);
  };

  const handleContinue = () => {
    if (!addressValidated && formData.address) {
      setShowModal(true);
      return;
    }

    setIsNewProperty(isNewProperty);

    if (isCommerce) {
      const surfaceRdcNum = parseFloat(surfaceRdc);
      const surfaceSousSolNum = surfaceSousSol ? parseFloat(surfaceSousSol) : 0;
      
      const validation = validateData(step3Schema, {
        address: formData.address,
        surfaceRdc: surfaceRdcNum,
        surfaceSousSol: surfaceSousSolNum,
      });

      if (!validation.success) {
        setErrors(validation.errors || {});
        return;
      }

      setSurfaceRdc(surfaceRdcNum);
      setSurfaceSousSol(surfaceSousSolNum);
    } else {
      const surfaceNum = parseFloat(surface);
      const validation = validateData(step3Schema, {
        address: formData.address,
        surface: surfaceNum,
      });

      if (!validation.success) {
        setErrors(validation.errors || {});
        return;
      }

      setSurface(surfaceNum);
    }
    
    nextStep();
  };

  return (
    <div className="animate-fade-in">
      <h2 className="text-[28px] font-light tracking-tight mb-2">Détails du bien</h2>
      <p className="text-sm text-secondary mb-10">
        Renseignez les informations du bien
      </p>

      <AddressAutocomplete
        value={formData.address || ''}
        onChange={handleAddressChange}
        error={errors.address}
      />

      {isCommerce ? (
        <>
          <Input
            label="Surface RDC *"
            type="number"
            value={surfaceRdc}
            onChange={handleSurfaceRdcChange}
            placeholder="150"
            min="5"
            addon="m²"
            error={errors.surfaceRdc}
          />
          <Input
            label="Surface sous-sol"
            type="number"
            value={surfaceSousSol}
            onChange={handleSurfaceSousSolChange}
            placeholder="100"
            min="0"
            addon="m²"
            error={errors.surfaceSousSol}
          />
        </>
      ) : isBureaux ? (
        <>
          <Input
            label="Surface du bien *"
            type="number"
            value={surface}
            onChange={handleSurfaceChange}
            placeholder="250"
            min="5"
            addon="m²"
            error={errors.surface}
          />
          <Input
            label="Étage"
            type="number"
            value={floor}
            onChange={handleFloorChange}
            placeholder="3"
            min="0"
          />
        </>
      ) : (
        <Input
          label="Surface du bien *"
          type="number"
          value={surface}
          onChange={handleSurfaceChange}
          placeholder="250"
          min="5"
          addon="m²"
          error={errors.surface}
        />
      )}

      <div className="mb-6">
        <label className="group flex items-center cursor-pointer">
          <div className="relative">
            <input
              type="checkbox"
              checked={isNewProperty}
              onChange={handleIsNewPropertyChange}
              className="peer sr-only"
            />
            <div 
              className="w-5 h-5 rounded-md border-2 border-gray-300 bg-white transition-all peer-checked:border-gray-300 peer-focus:ring-2 peer-focus:ring-offset-2"
              style={{
                backgroundColor: isNewProperty ? 'var(--primary)' : 'white',
                borderColor: isNewProperty ? 'var(--primary)' : '#D1D5DB',
                '--tw-ring-color': 'var(--primary)'
              } as React.CSSProperties}
            >
            </div>
            <svg
              className="absolute top-0.5 left-0.5 w-4 h-4 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="3"
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900 transition-colors">
            Bien neuf
          </span>
        </label>
      </div>

      <div className="flex gap-3 mt-12">
        <Button variant="secondary" onClick={() => goToStep(2)}>
          Retour
        </Button>
        <Button variant="primary" onClick={handleContinue}>
          Continuer
        </Button>
      </div>

      <Modal
        isOpen={showModal}
        title="Sélection d'adresse requise"
        message="Pour garantir la précision de l'estimation, veuillez sélectionner une adresse dans la liste de suggestions."
        onClose={() => setShowModal(false)}
      />
    </div>
  );
};
